import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  AtsJobPostingSettingsHeaderFragment,
  AtsJobPostingSettingsHeaderPoliciesFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ArchiveJobPostingModal } from 'modules/ats/components/job_postings/ArchiveJobPostingModal'
import { CancelJobPostingModal } from 'modules/ats/components/job_postings/CancelJobPostingModal'
import { PublishJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal'
import { PublishedJobPostingModal } from 'modules/ats/components/job_postings/PublishJobPostingModal/PublishedJobPostingModal'
import { CompanyPageNotConfiguredModal } from 'modules/company_page/components/CompanyPageNotConfiguredModal'
import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'

interface Props {
  jobData: AtsJobPostingSettingsHeaderFragment
  isCompanyPageInstalled: boolean
  policies: AtsJobPostingSettingsHeaderPoliciesFragment
}

export const JobPostingSettingsHeader: React.FC<Props> = ({
  jobData,
  isCompanyPageInstalled,
  policies,
}) => {
  const navigate = useNavigate()
  const { publicUrl } = useCompanyPageSettings()
  const { company } = useCurrentSession()
  const location = useLocation()

  const { id, status, currentHiringRole } = jobData

  const [openedModal, setOpenedModal] = useState<
    'archive' | 'cancel' | 'company-page-warning' | 'publish' | 'published' | undefined
  >(location.state?.showPublishedModal ? 'published' : undefined)

  const isCompanyPageConfigured = !!company.slug && !!company.countryFactorialDomain
  const companyPageReady = isCompanyPageInstalled && isCompanyPageConfigured

  const careersUrl = publicUrl || ''

  const onPublish = () => {
    if (!companyPageReady) {
      setOpenedModal('company-page-warning')
      return
    }

    track(trackingEvents.ats.publishJobOpeningStart)
    setOpenedModal('publish')
  }

  const onClosePublishedJobPostingModal = () => {
    setOpenedModal(undefined)
    navigate(location.pathname, { replace: true })
  }

  const canManageJobPostings = policies.manageJobPostings.can
  const isReviewerRole = currentHiringRole[0]?.hiringRole === 'reviewer'
  const isEditorRole = currentHiringRole[0]?.hiringRole === 'editor'

  const isOwnerRole = canManageJobPostings || (!isReviewerRole && !isEditorRole)

  return (
    <>
      <ResourceHeader
        title={i18n.t('ats.job_posting_settings.title')}
        primaryAction={
          isOwnerRole
            ? {
                label:
                  status === 'published'
                    ? i18n.t('ats.job_details.status_options.publish_unlisted')
                    : i18n.t('ats.job_details.status_options.publish'),
                onClick: () => onPublish(),
              }
            : undefined
        }
        otherActions={
          isOwnerRole
            ? [
                status !== 'archived' && {
                  label: i18n.t('ats.job_details.status_options.archive'),
                  onClick: () => setOpenedModal('archive'),
                },
                status !== 'cancelled' && {
                  label: i18n.t('ats.job_details.status_options.cancel'),
                  onClick: () => setOpenedModal('cancel'),
                },
              ].filter(Boolean)
            : undefined
        }
      />

      {openedModal === 'archive' && (
        <ArchiveJobPostingModal
          careersUrl={careersUrl}
          id={id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'cancel' && (
        <CancelJobPostingModal
          careersUrl={careersUrl}
          id={id}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'publish' && (
        <PublishJobPostingModal
          job={jobData}
          onSuccess={(value) => {
            setOpenedModal(undefined)
            value === 'published' && setOpenedModal('published')
          }}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
      {openedModal === 'published' && (
        <PublishedJobPostingModal id={id} onClose={() => onClosePublishedJobPostingModal()} />
      )}
      {openedModal === 'company-page-warning' && (
        <CompanyPageNotConfiguredModal onClose={() => setOpenedModal(undefined)} />
      )}
    </>
  )
}
